// MapLibre GL wrapper for the SeaState console.
//
// This is the one big justified runtime dependency (see docs/CONVENTIONS.md):
// a full vector/raster map engine is not something we write ourselves. Everything
// else stays platform-native. The wrapper exposes a tiny, stable surface so the
// rest of the app never touches MapLibre types directly and MapLibre can be
// lazy-loaded / code-split behind this boundary later.
import maplibregl from "maplibre-gl";
import type { Map as MlMap, StyleSpecification } from "maplibre-gl";
// MapLibre's stylesheet is copied to dist/maplibre-gl.css by build.mjs and linked
// from index.html — not imported here, so esbuild doesn't emit a redundant bundle.css.

/** Live handle to the map, returned by {@link createMap}. */
export interface SeaMap {
  /** Move the marker to a new point. Does NOT recenter the map. */
  setPoint(lat: number, lon: number): void;
  /** Run a callback once the underlying map has loaded its style. */
  onReady(cb: () => void): void;
  /**
   * Pin an element to the site marker, so it tracks the point through pan,
   * zoom and drag without the caller ever projecting a coordinate.
   *
   * This exists so instruments that belong AT the point (the socks) can be
   * plain DOM+canvas written against nothing but the palette, while the one
   * module that is allowed to know about MapLibre keeps the positioning. The
   * element is expected to be pointer-transparent except on its own controls —
   * anything opaque here is map the operator can no longer click.
   *
   * @returns a detach function.
   */
  attachAtPoint(el: HTMLElement, offset: [number, number]): () => void;
  /** The raw MapLibre map — for the overlays module ONLY; everything else
   *  stays behind this wrapper's surface. */
  raw(): MlMap;
  /** Tear down the map and release its WebGL context. */
  destroy(): void;
}

export interface MapOptions {
  /** Initial center as [lon, lat] (MapLibre order). */
  center: [number, number];
  zoom: number;
  /** Called with (lat, lon) whenever the user clicks the map. */
  onPick: (lat: number, lon: number) => void;
  /**
   * Called while the site marker is being dragged, then once more on drop.
   *
   * Dragging is the theater model's payoff: the whole operating area is already
   * resident, so moving the site re-derives the stack from memory. The live
   * signal is what makes that legible — a marker that only reports on drop feels
   * like a slow click, not like moving a point through a field. Hosts should do
   * the cheap in-memory work on `settled: false` and the network work (if any)
   * only when `settled` is true.
   */
  onDrag?: (lat: number, lon: number, settled: boolean) => void;
}

/** Navy marker colour, matched to the brand ink. */
const MARKER_COLOR = "#16232e";

/**
 * The site mark: a small dot with a slow pulse, in place of MapLibre's default
 * teardrop pin.
 *
 * The pin was the wrong shape for what now stands here. A teardrop is 40 px of
 * opaque ink that hangs ABOVE its own coordinate — it covers the water north of
 * the point, and the point is exactly where the socks fly from, so the pin sat
 * across the instrument reading. A dot is at its coordinate rather than above
 * it, and takes 13 px.
 *
 * The pulse is what a pin's size used to buy: findability. After a pan across an
 * ocean of moving arrows, a still dot is hard to catch; a slow expanding ring is
 * caught by peripheral vision without being read. It is slow (2.4 s) and faint
 * on purpose — this is a bridge instrument, and a fast pulse next to a go/no-go
 * colour reads as an alarm.
 *
 * The white collar is not decoration: the dot sits over a live colour ramp, and
 * navy on the deep blue of a 40 kn field is a dot that is not there.
 *
 * Animated through the Web Animations API rather than a keyframe rule, so the
 * whole mark stays inside this module with nothing to keep in step in the
 * stylesheet — and so prefers-reduced-motion is a branch, not a media query.
 */
function siteDot(): HTMLElement {
  const wrap = document.createElement("div");
  // Absolute, not relative — see the note in map/socks.ts. This element only
  // escaped the same bug by being the FIRST marker in the container, where the
  // static offset happens to be zero. That is luck, not correctness.
  wrap.style.cssText =
    "position:absolute;top:0;left:0;width:14px;height:14px;pointer-events:auto;" +
    // Above the pinned instruments, which are added after it: the point must
    // never end up underneath the flags that belong to it.
    "z-index:3;";

  const ring = document.createElement("div");
  ring.style.cssText =
    "position:absolute;inset:0;border-radius:50%;" +
    `border:2px solid ${MARKER_COLOR};opacity:0;pointer-events:none;`;

  const core = document.createElement("div");
  core.style.cssText =
    "position:absolute;left:50%;top:50%;width:11px;height:11px;" +
    "transform:translate(-50%,-50%);border-radius:50%;" +
    `background:${MARKER_COLOR};border:2px solid #fff;` +
    "box-shadow:0 0 0 1px rgba(22,35,46,0.35);";

  wrap.append(ring, core);

  const still = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true;
  if (!still && typeof ring.animate === "function") {
    ring.animate(
      [
        { transform: "scale(0.85)", opacity: 0.5 },
        { transform: "scale(2.4)", opacity: 0 },
      ],
      { duration: 2400, iterations: Infinity, easing: "cubic-bezier(0.2,0.6,0.4,1)" },
    );
  }
  return wrap;
}

// Esri "Ocean" raster basemap — CORS-friendly, no key required, same tiles as v1.
// Base imagery + a separate reference (labels) overlay, composited as two layers.
const OCEAN_TILES =
  "https://server.arcgisonline.com/ArcGIS/rest/services/Ocean/World_Ocean_Base/MapServer/tile/{z}/{y}/{x}";
const LABEL_TILES =
  "https://server.arcgisonline.com/ArcGIS/rest/services/Ocean/World_Ocean_Reference/MapServer/tile/{z}/{y}/{x}";

// Both sources are capped at z10; deeper zooms scale up (overzoom) the z10
// tiles instead of fetching new ones. Probed tile-by-tile (2026-08): the base
// has real detail past z10 only along US coasts — in TDI's working regions
// (Guyana, Nigeria, the Amazon delta) z11+ returns a literal "Map data not yet
// available" placeholder tile, so before this cap a deep zoom off Guyana was a
// wall of placeholder text. The labels are blank past z10 everywhere except US
// metro street names, which a marine console doesn't need. The cap also means
// zooming past 10 costs zero tile requests, which is most of the "map fills in
// as I move" complaint.
const BASEMAP_MAXZOOM = 10;

function oceanStyle(): StyleSpecification {
  return {
    version: 8,
    sources: {
      ocean: {
        type: "raster",
        tileSize: 256,
        maxzoom: BASEMAP_MAXZOOM,
        tiles: [OCEAN_TILES],
        attribution: "Esri Ocean Basemap",
      },
      labels: {
        type: "raster",
        tileSize: 256,
        maxzoom: BASEMAP_MAXZOOM,
        tiles: [LABEL_TILES],
      },
    },
    // raster-fade-duration 0 kills MapLibre's 300 ms per-tile crossfade — the
    // visible patchy "fill in" as tiles arrive. It is this paint property that
    // governs raster tiles; the map-level fadeDuration option only fades symbols.
    layers: [
      { id: "ocean", type: "raster", source: "ocean", paint: { "raster-fade-duration": 0 } },
      { id: "labels", type: "raster", source: "labels", paint: { "raster-fade-duration": 0 } },
    ],
  };
}

export function createMap(container: HTMLElement, opts: MapOptions): SeaMap {
  let ready = false;
  const map: MlMap = new maplibregl.Map({
    container,
    style: oceanStyle(),
    center: opts.center,
    zoom: opts.zoom,
    // Per-source LRU of retired tiles. The default is viewport-derived
    // (~100 tiles at laptop size), small enough that a three-viewport pan and
    // return already re-requested 11 tiles — each re-entering with the fade
    // that reads as the map "filling in". 256 ≈ ten screenfuls per source;
    // decoded raster tiles run ~0.26 MB each, so the worst case is ~70 MB per
    // source, and with the z10 cap a session rarely retires that many.
    // (prefetchZoomDelta is Mapbox-only; MapLibre 4.x has no such option.)
    maxTileCacheSize: 256,
  });
  map.once("load", () => {
    ready = true;
  });

  map.addControl(new maplibregl.NavigationControl(), "top-left");

  const draggable = opts.onDrag !== undefined;
  // anchor "center": a dot marks its own coordinate. The default pin anchors at
  // its tip, which is what let it hang over the water north of the point.
  const marker = new maplibregl.Marker({ element: siteDot(), anchor: "center", draggable })
    .setLngLat(opts.center)
    .addTo(map);
  if (draggable) marker.getElement().style.cursor = "grab";

  // Markers riding along with the site. They follow the RAW (unwrapped) LngLat
  // the site marker holds, not the wrapped copy handed to callers: on a world
  // copy past ±180 the wrapped value would fling them a world away from the pin
  // they belong to.
  const pinned: maplibregl.Marker[] = [];
  const syncPinned = (): void => {
    const ll = marker.getLngLat();
    for (const m of pinned) m.setLngLat(ll);
  };

  marker.on("drag", () => {
    syncPinned();
    const p = marker.getLngLat().wrap();
    opts.onDrag?.(p.lat, p.lng, false);
  });
  marker.on("dragend", () => {
    syncPinned();
    // wrap(): with renderWorldCopies a drag on a world copy reports lng past
    // ±180, and every per-point endpoint (SVP nearest-cell, NCEI, ERDDAP)
    // treats raw longitude as unwrapped — a mid-Pacific point one world east
    // would read as "no data" everywhere.
    const p = marker.getLngLat().wrap();
    opts.onDrag?.(p.lat, p.lng, true);
  });

  map.on("click", (e) => {
    marker.setLngLat(e.lngLat);
    syncPinned();
    const p = e.lngLat.wrap(); // same world-copy wrap as dragend above
    opts.onPick(p.lat, p.lng);
  });

  return {
    setPoint(lat: number, lon: number): void {
      marker.setLngLat([lon, lat]);
      syncPinned();
    },
    attachAtPoint(el: HTMLElement, offset: [number, number]): () => void {
      const m = new maplibregl.Marker({ element: el, anchor: "center", offset })
        .setLngLat(marker.getLngLat())
        .addTo(map);
      pinned.push(m);
      return (): void => {
        m.remove();
        const i = pinned.indexOf(m);
        if (i >= 0) pinned.splice(i, 1);
      };
    },
    onReady(cb: () => void): void {
      // "Loaded" has to mean "the style has loaded once", not MapLibre's
      // map.loaded(), which goes FALSE again whenever a source or layer is added
      // and tiles are in flight. Judged by that, a callback registered just
      // after another callback added a layer was told "not yet", attached to
      // the load event — which had already fired — and never ran. The thermal
      // console's sea-surface field was added exactly that way, after the
      // imagery layer, and was captioned, loaded and never drawn (Cody,
      // 2026-10-06: "I've never even seen it on the map?").
      if (ready || map.loaded()) cb();
      else map.once("load", cb);
    },
    raw(): MlMap {
      return map;
    },
    destroy(): void {
      map.remove();
    },
  };
}
